import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A sheet over the page (from the bottom on a phone, centred on a computer). Rendered
 * on the body: the page's entry animation would trap a fixed overlay. Escape or a tap
 * outside closes it.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const id = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCloseRef.current();
      // Tab goes round inside the sheet, never to the page behind it
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex="0"]',
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('keydown', onKey);
      // Back to the button that opened it
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <div
      className="sim-figures fixed inset-0 z-30 flex items-end justify-center bg-night/60 p-0 sm:items-center sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        className="animate-rise max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-cream px-5 pt-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-3xl dark:bg-night-800"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 id={id} className="font-serif text-2xl font-semibold text-purple dark:text-gold-300">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="rounded-full px-3 py-2 text-sm font-medium hover:bg-cream-200 dark:hover:bg-night-700"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
